import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { searchApi, SearchParams } from '@/services/searchApi';
import { ApiClientError } from '@/services/apiClient';
import type { SearchResponse, SearchResult, SearchType } from '@/types/search';

export type SearchStatus = 'idle' | 'loading' | 'success' | 'error';

export interface SearchState {
  status: SearchStatus;
  query: string;
  type: SearchType;
  page: number;
  totalPages: number;
  totalResults: number;
  results: SearchResult[];
  error: string | null;
  errorCode: string | null;
}

const initialState: SearchState = {
  status: 'idle',
  query: '',
  type: 'multi',
  page: 1,
  totalPages: 0,
  totalResults: 0,
  results: [],
  error: null,
  errorCode: null,
};

function describeError(err: unknown): { code: string; message: string } {
  if (err instanceof ApiClientError) return { code: err.code, message: err.message };
  if (err instanceof Error) return { code: 'UNKNOWN', message: err.message };
  return { code: 'UNKNOWN', message: 'Unknown error' };
}

export const runSearch = createAsyncThunk<
  SearchResponse,
  SearchParams,
  { rejectValue: { code: string; message: string } }
>('search/run', async (params, { rejectWithValue, signal }) => {
  try {
    // Forward the thunk's AbortSignal so promise.abort() in the consumer
    // actually cancels the in-flight fetch and prevents a stale response
    // from clobbering a newer one.
    return await searchApi.search({ ...params, signal });
  } catch (err) {
    return rejectWithValue(describeError(err));
  }
});

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    resetSearch() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSearch.pending, (state, action) => {
        state.status = 'loading';
        state.query = action.meta.arg.query;
        state.type = action.meta.arg.type ?? 'multi';
        state.page = action.meta.arg.page ?? 1;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(runSearch.fulfilled, (state, action) => {
        state.status = 'success';
        state.results = action.payload.results;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(runSearch.rejected, (state, action) => {
        // The Results page aborts the previous request when q/type changes.
        // Aborted thunks reject with a DOMException — ignore so the UI doesn't
        // flicker into an error state between cancellation and the next pending.
        if (action.meta.aborted) return;
        state.status = 'error';
        state.results = [];
        state.totalPages = 0;
        state.totalResults = 0;
        state.error = action.payload?.message ?? action.error.message ?? 'Search failed';
        state.errorCode = action.payload?.code ?? 'UNKNOWN';
      });
  },
});

export const { resetSearch } = searchSlice.actions;
export default searchSlice.reducer;
